/**
 * SequencerGrid Component - Main grid interface for composing music
 * Manages drag state, auto-scroll, and renders all grid rows
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GridRow } from './GridRow';
import type { SequencerGridProps, DragState, GridRowProps } from '../../types/grid';

// Module-level counter for generating unique row IDs
let nextRowId = 0;
function generateRowId(): number {
  return nextRowId++;
}

/**
 * SequencerGrid component
 * Renders the complete sequencer grid with:
 * - Multiple rows of cells
 * - Drag-to-create functionality
 * - Auto-scroll to current step
 * - Add row button
 * 
 * @param props - Grid properties
 * @returns SequencerGrid component
 */
export function SequencerGrid({
  gridState,
  noteState,
  volumeState,
  muted,
  instrumentNames,
  instrumentParameters,
  parametersEnabled,
  effectsChains,
  effectsPanelExpanded,
  steps,
  currentStep,
  onCellToggle,
  onNoteChange,
  onInstrumentChange,
  onParametersChange,
  onParametersEnabledChange,
  onVolumeChange,
  onMuteToggle,
  onAddRow,
  onRemoveRow,
  onEffectsChange,
  onEffectsPanelToggle,
}: SequencerGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    dragRow: null,
    dragStart: null,
    dragEnd: null,
    dragOccurred: false,
  });

  // Stable unique IDs for each row so React can track identity across add/remove
  const [rowIds, setRowIds] = useState<number[]>(() =>
    gridState.map(() => generateRowId())
  );

  // Keep rowIds in sync when rows are added or removed
  const prevRowCountRef = useRef(gridState.length);
  useEffect(() => {
    const prevCount = prevRowCountRef.current;
    const newCount = gridState.length;
    prevRowCountRef.current = newCount;

    if (newCount > prevCount) {
      // Rows added at the end
      setRowIds(prev => {
        const additional = Array.from({ length: newCount - prev.length }, () => generateRowId());
        return [...prev, ...additional];
      });
    } else if (newCount < prevCount) {
      // Row removed — trim to match. The actual removal index is handled
      // by the onRemoveRow callback which we wrap below.
      // (trimming is done in the wrapped handler)
    }
  }, [gridState.length]);

  /**
   * Track if we've scrolled during playback
   */
  const hasScrolledRef = useRef(false);

  /**
   * Auto-scroll to current step during playback
   * Only scrolls after the first step change to avoid centering on load
   */
  useEffect(() => {
    if (!gridRef.current || currentStep < 0) return;

    // Don't auto-scroll on initial load (currentStep 0)
    if (currentStep === 0 && !hasScrolledRef.current) {
      return;
    }

    hasScrolledRef.current = true;

    const grid = gridRef.current;
    const cells = grid.querySelectorAll(`[data-step="${currentStep}"]`);
    
    if (cells.length === 0) return;

    const cell = cells[0] as HTMLElement;
    const gridWidth = grid.clientWidth;
    const cellCenter = cell.offsetLeft + cell.clientWidth / 2;
    const targetScroll = Math.max(0, cellCenter - gridWidth / 2);

    grid.scrollTo({ left: targetScroll, behavior: 'smooth' });
  }, [currentStep]);

  /**
   * Reset scroll tracking and position when playback stops (currentStep resets to 0)
   */
  useEffect(() => {
    if (currentStep === 0 && hasScrolledRef.current && gridRef.current) {
      // Scroll back to the left
      gridRef.current.scrollTo({ left: 0, behavior: 'smooth' });
      hasScrolledRef.current = false;
    }
  }, [currentStep]);

  /**
   * Handle mouse down - start drag
   */
  const handleMouseDown = useCallback((row: number, step: number) => {
    setDragState({
      isDragging: true,
      dragRow: row,
      dragStart: step,
      dragEnd: step,
      dragOccurred: false,
    });
  }, []);

  /**
   * Handle mouse over - continue drag
   */
  const handleMouseOver = useCallback(
    (row: number, step: number) => {
      if (dragState.isDragging && dragState.dragRow === row) {
        setDragState((prev) => ({
          ...prev,
          dragEnd: step,
          dragOccurred: true,
        }));
      }
    },
    [dragState.isDragging, dragState.dragRow]
  );

  /**
   * Handle mouse up - end drag
   */
  useEffect(() => {
    const handleMouseUp = () => {
      if (
        dragState.isDragging &&
        dragState.dragOccurred &&
        dragState.dragRow !== null &&
        dragState.dragStart !== null &&
        dragState.dragEnd !== null
      ) {
        const row = dragState.dragRow;
        const start = Math.min(dragState.dragStart, dragState.dragEnd);
        const end = Math.max(dragState.dragStart, dragState.dragEnd);

        // Toggle all cells in the drag range
        for (let step = start; step <= end; step++) {
          const rowData = gridState[row];
          if (rowData && !rowData[step]) {
            onCellToggle(row, step);
          }
        }
      }

      setDragState({
        isDragging: false,
        dragRow: null,
        dragStart: null,
        dragEnd: null,
        dragOccurred: false,
      });
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [dragState, gridState, onCellToggle]);

  /**
   * Handle cell toggle with drag support
   */
  const handleCellToggle = (row: number, step: number) => {
    // Only toggle if not dragging or if it's a single click
    if (!dragState.dragOccurred) {
      onCellToggle(row, step);
    }
  };

  /**
   * Memoize row rendering to prevent unnecessary re-renders
   */
  const rows = useMemo(() => {
    return gridState.map((rowData, rowIndex) => {
      const instrument = instrumentNames[rowIndex];
      const volume = volumeState[rowIndex];
      const mutedState = muted[rowIndex];
      const notes = noteState[rowIndex];
      const parameters = instrumentParameters?.[rowIndex];

      // Skip row if data is missing
      if (!instrument || volume === undefined || mutedState === undefined || !notes) {
        return null;
      }

      const channelData: GridRowProps['channel'] = {
        instrument,
        volume,
        muted: mutedState,
      };
      
      if (parameters !== undefined) {
        channelData.parameters = parameters;
      }
      
      if (parametersEnabled !== undefined && parametersEnabled[rowIndex] !== undefined) {
        channelData.parametersEnabled = parametersEnabled[rowIndex];
      }

      const gridRowProps: GridRowProps = {
        row: rowIndex,
        channel: channelData,
        steps,
        currentStep,
        gridState: rowData,
        noteState: notes,
        effectsChain: effectsChains?.[rowIndex] || [],
        effectsPanelExpanded: effectsPanelExpanded?.[rowIndex] || false,
        onCellToggle: (step) => handleCellToggle(rowIndex, step),
        onNoteChange: (step, note) => onNoteChange(rowIndex, step, note),
        onInstrumentChange: (instrument, params) => onInstrumentChange(rowIndex, instrument, params),
        onVolumeChange: (volume) => onVolumeChange(rowIndex, volume),
        onMuteToggle: () => onMuteToggle(rowIndex),
        onRemoveRow: () => {
          setRowIds(prev => prev.filter((_, i) => i !== rowIndex));
          onRemoveRow(rowIndex);
        },
      };
      
      if (onParametersChange) {
        gridRowProps.onParametersChange = (params) => onParametersChange(rowIndex, params);
      }
      
      if (onParametersEnabledChange) {
        gridRowProps.onParametersEnabledChange = (enabled) => onParametersEnabledChange(rowIndex, enabled);
      }
      
      if (onEffectsChange) {
        gridRowProps.onEffectsChange = (effects) => onEffectsChange(rowIndex, effects);
      }
      
      if (onEffectsPanelToggle) {
        gridRowProps.onEffectsPanelToggle = () => onEffectsPanelToggle(rowIndex);
      }

      return (
        <div
          key={`row-${rowIds[rowIndex] ?? rowIndex}`}
          onMouseDown={() => handleMouseDown(rowIndex, 0)}
          onMouseOver={() => handleMouseOver(rowIndex, 0)}
        >
          <GridRow {...gridRowProps} />
        </div>
      );
    });
  }, [
    gridState,
    instrumentNames,
    instrumentParameters,
    parametersEnabled,
    effectsChains,
    effectsPanelExpanded,
    volumeState,
    muted,
    noteState,
    steps,
    currentStep,
    handleMouseDown,
    handleMouseOver,
    handleCellToggle,
    onNoteChange,
    onInstrumentChange,
    onParametersChange,
    onParametersEnabledChange,
    onVolumeChange,
    onMuteToggle,
    onRemoveRow,
    onEffectsChange,
    onEffectsPanelToggle,
  ]);

  return (
    <div
      id="sequencer-container"
      className="w-full box-border overflow-x-auto"
      ref={gridRef}
    >
      <div
        id="grid"
        className="flex flex-col box-border"
        style={{ minWidth: 'max-content', gap: '12px' }}
      >
        {rows}

        {/* Add Row Button */}
        <div className="grid-row">
          <div className="row-controls">
            {/* Spacer to match instrument select width */}
            <div style={{ width: '8rem', minWidth: '8rem' }}></div>
            
            {/* Spacer to match mute button width */}
            <div style={{ width: '2rem', minWidth: '44px' }}></div>
            
            {/* Spacer to match volume slider width */}
            <div style={{ width: '3.5rem' }}></div>
            
            {/* Add button in place of remove button */}
            <div
              className="add-row text-xl font-bold cursor-pointer text-center hover:text-accent"
              style={{ width: '2.5rem', height: '2.5rem', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={onAddRow}
              role="button"
              aria-label="Add new row"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onAddRow();
                }
              }}
            >
              +
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
